import type { BeatType, CardDef, Outcome, StoryElement } from '../types.js';
import { VisualRecipeSchema } from '../types.js';
import { consequenceDef } from '../story/consequence.js';

const BEAT_CONTEXT: Record<BeatType, string> = {
  character: '{element} blinks, as if hearing a draft revision.',
  place: '{element} redraws itself in warmer ink.',
  object: '{element} wobbles, then accepts its new fate.',
  event: '{element} stutters, then continues on your terms.',
  mood: 'The mood around {element} turns sideways, briefly.',
};

export interface TemplateContext {
  card: CardDef;
  beatType: BeatType;
  element: StoryElement;
  variables: Record<string, string | number | boolean>;
  stateKey: string;
  sceneId: string;
  backgroundId?: string;
  storyId: string;
  beatId: string;
}

function fill(template: string, ctx: TemplateContext): string {
  let out = template;
  const vars: Record<string, string> = {
    card_name: ctx.card.name,
    element: ctx.element.label,
    scene: ctx.sceneId,
    state: ctx.stateKey,
  };
  for (const [k, v] of Object.entries(ctx.variables)) {
    vars[k] = String(v);
  }
  for (const [k, v] of Object.entries(vars)) {
    out = out.replaceAll(`{${k}}`, v);
  }
  return out;
}

export function buildTemplateOutcome(ctx: TemplateContext): Outcome {
  const beatLine = fill(BEAT_CONTEXT[ctx.beatType], ctx);
  const label = ctx.element.id === 'villain' && typeof ctx.variables.villain === 'string' ? ctx.variables.villain : ctx.element.label;
  const now = consequenceDef(ctx.card).now.replaceAll('{t}', label);
  const bridge = `${now[0].toUpperCase()}${now.slice(1)} ${beatLine} The narrator pauses, then pretends this was always canon.`;
  const quip =
    ctx.card.type === 'rule_breaker'
      ? 'Fine. FINE. We can do that. Temporarily.'
      : ctx.card.type === 'chaos'
        ? 'That was not on my outline. It is now.'
        : 'You interrupted my perfect sentence. Again.';

  const effects: Outcome['effects'] = [
    { type: 'narrator_glitch', value: 'rewrite' },
    {
      type: 'shift_track',
      target:
        ctx.card.type === 'chaos'
          ? 'chaos'
          : ctx.card.type === 'keepsake'
            ? 'hope'
            : 'fate',
      value: 1,
    },
  ];

  if (ctx.card.id === 'rule_skip_intent') {
    effects.push({ type: 'set_variable', target: 'intent_blocked', value: true });
  }
  if (ctx.card.type === 'counter') {
    effects.push({ type: 'shift_dread', value: -1 });
  } else if (ctx.card.type === 'chaos') {
    effects.push({ type: 'shift_dread', value: 1 });
  }

  if (ctx.card.type === 'keepsake') {
    effects.push({
      type: 'apply_keepsake',
      target: ctx.element.id,
      value: ctx.card.id,
    });
  }

  const mood =
    ctx.card.type === 'chaos'
      ? 'chaos'
      : ctx.stateKey.includes('dread')
        ? 'dread'
        : 'tense';

  const visual = VisualRecipeSchema.parse({
    backgroundId: ctx.backgroundId ?? ctx.sceneId,
    mood,
    overlayIds: [`overlay_${ctx.card.type}`],
    narratorExpression: 'flustered',
    stampEffect: ctx.card.type === 'twist' ? 'stamp_twist' : undefined,
  });

  const key = `${ctx.storyId}/${ctx.beatId}/${ctx.card.id}/${ctx.stateKey}`;

  return {
    key,
    tier: 'template',
    bridgeText: bridge,
    narratorQuip: quip,
    effects,
    visual,
    callback: ctx.card.type === 'twist'
      ? `Remember ${ctx.card.name} on ${ctx.element.label}? The story does.`
      : undefined,
  };
}

export function buildFallbackOutcome(ctx: TemplateContext): Outcome {
  const base = buildTemplateOutcome(ctx);
  return {
    ...base,
    tier: 'fallback',
    bridgeText: `Something about ${ctx.card.name} lands wrong, then right. ${ctx.element.label} survives the edit. The narrator improvises with dignity bruised.`,
    narratorQuip: 'I am choosing to believe you meant that.',
  };
}
